import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays } from 'lucide-react';
import { Button } from './ui/button';
import EventCard from './EventCard';
import { NoUpcomingEvents } from './NoUpcomingEvents';
import { getFeaturedEvents } from '../data/events';

/**
 * Homepage "Upcoming Events" section.
 * Compact showcase of featured events + link to full Events page.
 */
export default function UpcomingEvents() {
  const events = getFeaturedEvents();

  return (
    <section
      className="relative py-20 sm:py-28 overflow-hidden"
      data-testid="upcoming-events-section"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950 to-slate-900/50" />
      <div className="absolute top-1/4 -left-24 w-80 h-80 rounded-full bg-violet-700/15 blur-[100px]" />
      <div className="absolute bottom-1/4 -right-24 w-96 h-96 rounded-full bg-amber-500/10 blur-[110px]" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section head */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10 sm:mb-14">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-widest text-slate-300 mb-4">
              <CalendarDays className="h-3.5 w-3.5 text-amber-400" />
              Upcoming Events
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-tight">
              Train live with
              <span className="block bg-gradient-to-r from-violet-400 via-amber-300 to-violet-400 bg-clip-text text-transparent">
                The WE Mentality
              </span>
            </h2>
            <p className="mt-4 text-slate-300 text-base sm:text-lg max-w-2xl leading-relaxed">
              Immersive experiences for teams, leaders, and the next generation of entrepreneurs.
            </p>
          </div>

          <Button
            asChild
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10 hover:text-white bg-transparent self-start sm:self-end whitespace-nowrap"
          >
            <Link to="/events" data-testid="view-all-events-btn">
              View All Events
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>

        {/* Cards */}
        {events.length === 0 ? <NoUpcomingEvents /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-7">
            {events.map((event) => (
              <EventCard key={event.slug} event={event} variant="compact" />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
